import { useEffect, useRef } from 'react';

import * as evaluateExpression from '@/helpers/evaluateExpression';
import trackEvents from '@/helpers/trackEvents';
import type * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';

type BaseFieldTemplate = Pick<
  QuestionnaireApiModels.FieldTemplate,
  'key' | 'label' | 'rank' | 'type' | 'subsection' | 'listFieldTemplateId' | 'autofill' | 'conditionExpression'
> & {
  listItemTemplates?: Array<Pick<QuestionnaireApiModels.FieldTemplate, 'key'>>;
};

type GetResponseSource = (key: string) => QuestionnaireApiModels.QuestionnaireResponse['source'];

type Props = {
  fieldsByKey: Record<string, BaseFieldTemplate>;
  formMethods: {
    getValues: (key?: string) => unknown;
  };
  getResponseSource: GetResponseSource;
  sections: Array<
    Pick<QuestionnaireApiModels.SectionTemplate, 'key' | 'label' | 'rank' | 'subsections'> & {
      fields: Array<Pick<QuestionnaireApiModels.FieldTemplate, 'key' | 'listFieldTemplateId'>>;
    }
  >;
};

const buildField = ({
  field,
  fieldsByKey,
  formMethods,
  getResponseSource,
  valueKeyPrefix,
}: {
  field: BaseFieldTemplate;
  fieldsByKey: Props['fieldsByKey'];
  formMethods: Props['formMethods'];
  getResponseSource: GetResponseSource;
  valueKeyPrefix?: string;
}): Record<string, unknown> => {
  const thisField: Record<string, unknown> = {
    autofill: field.autofill,
    conditionExpression: field.conditionExpression,
    key: field.key,
    label: field.label,
    rank: field.rank,
    type: field.type,
    valueKeyPrefix,
  };

  if (field.listItemTemplates && field.listItemTemplates.length) {
    if (field.type === 'list' || field.type === 'table') {
      const rowValues = formMethods.getValues(field.key);
      if (Array.isArray(rowValues)) {
        thisField.rows = rowValues.map((rowValue, rowIndex) => {
          const columns = (field.listItemTemplates || []).map(({ key }) => {
            const listItemTemplate = fieldsByKey[key];
            return {
              [listItemTemplate.label]: buildField({
                field: listItemTemplate,
                fieldsByKey,
                formMethods,
                getResponseSource,
                valueKeyPrefix: `${field.key}[${rowIndex}]`,
              }),
            };
          });
          return { columns, id: rowValue._id };
        });
      } else {
        thisField.rows = [];
      }
    } else {
      const children: unknown[] = [];

      field.listItemTemplates.forEach(({ key }) => {
        const listItemTemplate = fieldsByKey[key];
        children.push({
          [listItemTemplate.label]: buildField({
            field: listItemTemplate,
            fieldsByKey,
            formMethods,
            getResponseSource,
            valueKeyPrefix,
          }),
        });
      });
      thisField.children = children;
    }
  } else {
    const fullFieldKey = valueKeyPrefix ? `${valueKeyPrefix}.${field.key}` : field.key;

    thisField.value = formMethods.getValues(fullFieldKey);
    thisField.source = getResponseSource(fullFieldKey);
  }

  return thisField;
};

const createResponseSummary = ({
  formMethods,
  getResponseSource,
  sections,
  fieldsByKey,
}: {
  formMethods: Props['formMethods'];
  getResponseSource: GetResponseSource;
  sections: Props['sections'];
  fieldsByKey: Props['fieldsByKey'];
}): unknown => {
  const summary: unknown[] = sections.map((section) => {
    type Subsection = {
      [key: string]: unknown[];
    };

    const subsectionIdxMap = new Map<string, number>();
    const subsections: Subsection[] = [];

    (section.subsections ?? []).forEach((subsection) => {
      subsectionIdxMap.set(subsection, subsections.length);
      subsections.push({
        [subsection]: [],
      });
    });

    section.fields
      .filter((field) => !field.listFieldTemplateId)
      .forEach(({ key }) => {
        const field = fieldsByKey[key];
        if (!field) return;

        const subsection = field.subsection ?? '__NO_SUBSECTION__';
        let sectionIndex = subsectionIdxMap.get(subsection);
        if (sectionIndex === undefined) {
          sectionIndex = subsections.length;
          subsectionIdxMap.set(subsection, sectionIndex);
          subsections.push({
            [subsection]: [],
          });
        }
        subsections[sectionIndex][subsection].push({
          [field.label]: buildField({
            field,
            fieldsByKey,
            formMethods,
            getResponseSource,
          }),
        });
      });

    return {
      [section.label]: {
        key: section.key,
        rank: section.rank,
        subsections,
      },
    };
  });

  return summary;
};

const useQuestionnaireInspector = ({ fieldsByKey, formMethods, getResponseSource, sections }: Props): void => {
  const dataRef = useRef<Omit<Props, 'responseSources'>>({
    fieldsByKey,
    formMethods,
    getResponseSource,
    sections,
  });
  dataRef.current = {
    fieldsByKey,
    formMethods,
    getResponseSource,
    sections,
  };

  useEffect(() => {
    window.questionnaireInspector = {
      showFieldsByKey: () => console.dir(dataRef.current.fieldsByKey, { depth: null }),
      showFormValues: () => console.dir(dataRef.current.formMethods.getValues(), { depth: null }),
      showResponses: () => {
        console.dir(
          createResponseSummary({
            fieldsByKey: dataRef.current.fieldsByKey,
            formMethods: dataRef.current.formMethods,
            getResponseSource: dataRef.current.getResponseSource,
            sections: dataRef.current.sections,
          }),
          { depth: null },
        );
      },
    };

    window.evaluateExpressionProfiling = {
      disable: evaluateExpression.disableProfiling,
      enable: evaluateExpression.enableProfiling,
      log: () => {
        const { summary, countsById, cacheMemory, cacheSize } = evaluateExpression.getProfilingData();
        console.log('evaluateExpression profiling', {
          ...summary,
          cacheMemory: `${Math.round(cacheMemory / 1024)} KB`,
          cacheSize,
        });
        Object.keys(countsById)
          .sort((a, b) => countsById[b] - countsById[a])
          .forEach((id) => {
            console.log(`  ${id}: ${countsById[id]}`);
          });
      },
    };

    window.eventsProfiling = {
      disable: trackEvents.disable,
      enable: trackEvents.enable,
      log: () => {
        const counts = trackEvents.getCounts();
        console.log('Render counts');
        Object.keys(counts)
          .sort((a, b) => counts[b] - counts[a])
          .forEach((id) => {
            console.log(`  ${id}: ${counts[id]}`);
          });
      },
    };
    return () => {
      evaluateExpression.disableProfiling();
      delete window.questionnaireInspector;
      delete window.evaluateExpressionProfiling;
      delete window.eventsProfiling;
    };
  }, []);
};

export default useQuestionnaireInspector;
